/**
 * A single planted specimen.
 *
 * Descended from Niantic's `Flower.cs`, but the mechanism underneath has been
 * replaced. In the original — and in v1 of this project — a plant advanced
 * because you tapped it. It now advances because *time passed*.
 *
 * That is the one idea worth taking from Grow a Garden: you plant, you leave,
 * you live your life, and you come back to something that changed without
 * you. Nothing is taken from you for being away, and there is no failure
 * state. Growth is a pure function of the wall clock, which is also why
 * offline progress costs nothing to implement — there is no simulation to
 * catch up, only a subtraction.
 *
 * Tapping survives as *tending*: it buys time, plays the note and pulses the
 * plant, but it can never be the whole mechanism again. See `tend()`.
 */

import * as THREE from 'three';
import { buildPlant, type PlantModel } from '../models/plants';
import type { Species } from '../content/species';
import type { Mutation } from '../content/weather';
import { growMsBetween, mutationBetween, solveGrowTime } from './weather';

/** Growth stages between seed and maturity. Each crossing rings a note. */
export const STAGES = 5;

/**
 * Fraction of a plant's total grow time granted by one tend, before decay.
 * See `TEND_DECAY` for why this does not add up to a cheat.
 */
const TEND_GAIN = 0.1;

/**
 * Each subsequent tend on the same plant is worth 75% of the last. The series
 * converges: 0.1 + 0.075 + 0.056 … → 0.4 of the grow time, no matter how hard
 * anyone mashes. Tending is therefore always worth doing and never worth
 * grinding, which is the shape we want — a tap should feel generous, not
 * obligatory.
 */
const TEND_DECAY = 0.75;

export interface PlantState {
  speciesId: string;
  slot: number;
  seed: number;
  /** Epoch ms the seed went into the ground. */
  sownAt: number;
  /** Total ms of grow time bought by tending. */
  tendMs: number;
  /** How many times this specimen has been tended, for the decay curve. */
  tends: number;
}

export class Plant {
  readonly root = new THREE.Group();
  readonly species: Species;
  readonly slot: number;
  readonly seed: number;
  readonly sownAt: number;

  private model: PlantModel;
  /** Invisible cylinder used for tap picking — cheaper and far more forgiving
   *  than raycasting the plant's own geometry, which is spindly by design. */
  private picker: THREE.Mesh;
  private pickRadius: number;

  private growMs: number;
  private tendMs: number;
  private tends: number;

  private _stage = 0;
  private marked = false;
  private displayed = 0;
  private target = 0;
  private popTimer = 0;

  constructor(
    species: Species,
    slot: number,
    seed: number,
    sownAt: number,
    tendMs = 0,
    tends = 0,
  ) {
    this.species = species;
    this.slot = slot;
    this.seed = seed;
    this.sownAt = sownAt;
    this.growMs = species.growSeconds * 1000;
    this.tendMs = tendMs;
    this.tends = tends;

    this.model = buildPlant(species, seed);
    this.root.add(this.model.root);

    /*
     * The pick volume is a unit cylinder scaled every frame to match how big
     * the plant currently *is*. Sizing it to `matureHeight` up front was a
     * bug: a seedling then owned a 0.37 m radius of the plot, so the first
     * plant sown intercepted every later tap and nothing else could be sown.
     * The radius is also capped well inside the slot spacing so neighbouring
     * plants can never steal each other's taps.
     */
    this.pickRadius = Math.min(0.1, species.matureHeight * 0.15);
    this.picker = new THREE.Mesh(
      new THREE.CylinderGeometry(1, 1, 1, 6, 1),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    this.picker.userData.plant = this;
    this.root.add(this.picker);
    this.sizePicker();

    this.model.setGrowth(0);
  }

  /* ---------------------------------------------------------------- *
   * Clock
   * ---------------------------------------------------------------- */

  /** Effective grow time still owed before this specimen is mature. */
  private get owedMs(): number {
    return Math.max(0, this.growMs - this.tendMs);
  }

  /**
   * Epoch ms at which this specimen reaches maturity.
   *
   * Solved against the weather timeline rather than subtracted, because the
   * growth rate is no longer constant. It is derived from `sownAt` and
   * `tendMs`, both of which are serialised, so it survives a reload and every
   * reload agrees on it.
   */
  get readyAt(): number {
    return solveGrowTime(this.sownAt, this.owedMs);
  }

  /** 0–1 growth at an arbitrary point in time. */
  progressAt(nowMs: number): number {
    const grown =
      this.tendMs + growMsBetween(this.sownAt, nowMs, this.owedMs);
    return THREE.MathUtils.clamp(grown / this.growMs, 0, 1);
  }

  /** Stage 0–STAGES at an arbitrary point in time. */
  stageAt(nowMs: number): number {
    return Math.min(STAGES, Math.floor(this.progressAt(nowMs) * STAGES));
  }

  /**
   * The mark the weather left on this specimen, or null for an ordinary one.
   *
   * Recomputed from the window the plant grew in rather than stored. Both
   * ends of that window are derived from serialised values, so the answer is
   * stable across reloads and cannot drift out of step with the timeline that
   * produced it. Only a mature plant carries one — a mutation is what the
   * whole life amounted to, not a running total.
   */
  get mutation(): Mutation | null {
    if (!this.isMature) return null;
    return mutationBetween(this.sownAt, this.readyAt);
  }

  get stage(): number {
    return this._stage;
  }

  get isMature(): boolean {
    return this._stage >= STAGES;
  }

  /** Milliseconds until maturity, or 0 if already there. */
  remainingMs(nowMs: number): number {
    return Math.max(0, this.readyAt - nowMs);
  }

  /**
   * Bring the plant in line with the wall clock.
   *
   * Returns the number of stage boundaries crossed since the last call, so
   * the caller can ring that many notes — or, on a restore after hours away,
   * deliberately ring none.
   */
  syncToClock(nowMs: number, instant = false): number {
    this.target = this.progressAt(nowMs);
    const stage = this.stageAt(nowMs);
    const crossed = Math.max(0, stage - this._stage);
    this._stage = stage;
    if (instant) {
      this.displayed = this.target;
      this.model.setGrowth(this.displayed);
      this.sizePicker();
    }
    // The mark only lands once, at maturity, whether that happened just now
    // or eight hours ago in a tab nobody was watching.
    if (!this.marked && this.isMature) {
      this.marked = true;
      this.model.setMutation(this.mutation);
    }
    return crossed;
  }

  /**
   * Tend the plant: buy grow time, with the diminishing return described at
   * `TEND_DECAY`. Returns false when already mature so the caller can give a
   * softer acknowledgement instead of a reward.
   */
  tend(nowMs: number): boolean {
    if (this.isMature) return false;
    this.tendMs += this.growMs * TEND_GAIN * Math.pow(TEND_DECAY, this.tends);
    this.tends += 1;
    // Never let tending run the clock back past the moment of sowing.
    this.tendMs = Math.min(this.tendMs, this.growMs);
    // A short squash-and-stretch pulse on every tap, independent of the
    // slower growth tween. This is what makes tending feel physical.
    this.popTimer = 0.32;
    this.syncToClock(nowMs);
    return true;
  }

  /* ---------------------------------------------------------------- *
   * Presentation
   * ---------------------------------------------------------------- */

  /** The point bursts and score popups should originate from. */
  get crownWorldPosition(): THREE.Vector3 {
    return this.root.localToWorld(new THREE.Vector3(0, this.model.crownHeight(), 0));
  }

  get pickTarget(): THREE.Object3D {
    return this.picker;
  }

  /** Roughly how tall this plant stands right now, in metres. */
  get currentHeight(): number {
    return this.species.matureHeight * this.displayed;
  }

  /**
   * Match the pick cylinder to the plant's current height, with a floor so a
   * freshly-sown seed is still a comfortable phone target.
   */
  private sizePicker(): void {
    const h = Math.max(0.09, this.species.matureHeight * this.displayed);
    const r = Math.max(0.045, this.pickRadius * (0.55 + this.displayed * 0.45));
    this.picker.scale.set(r, h * 1.05, r);
    this.picker.position.y = (h * 1.05) / 2;
  }

  toState(): PlantState {
    return {
      speciesId: this.species.id,
      slot: this.slot,
      seed: this.seed,
      sownAt: this.sownAt,
      tendMs: this.tendMs,
      tends: this.tends,
    };
  }

  update(dt: number, time: number): void {
    // Ease the displayed growth toward the target. Frame-rate independent, so
    // it behaves the same at 30fps on a mid-range Android as at 90. On a slow
    // species the target barely moves and this is nearly a no-op; its real
    // job is absorbing the jump a tend produces.
    if (Math.abs(this.displayed - this.target) > 0.0005) {
      const k = 1 - Math.pow(0.0016, dt);
      this.displayed += (this.target - this.displayed) * k;
      this.model.setGrowth(this.displayed);
      this.sizePicker();
    }

    if (this.popTimer > 0) {
      this.popTimer = Math.max(0, this.popTimer - dt);
      const p = this.popTimer / 0.32;
      // Damped sine: overshoot, undershoot, settle.
      const pulse = Math.sin(p * Math.PI * 2) * p * 0.16;
      this.root.scale.set(1 + pulse * 0.6, 1 + pulse, 1 + pulse * 0.6);
    } else if (this.root.scale.x !== 1) {
      this.root.scale.setScalar(1);
    }

    // Mature plants sway a little more — they have more to sway.
    this.model.update(time + this.seed, 0.6 + this.displayed * 0.7);
  }

  dispose(): void {
    this.model.dispose();
    (this.picker.geometry as THREE.BufferGeometry).dispose();
    (this.picker.material as THREE.Material).dispose();
  }
}
